import React, { useState, useEffect } from "react";
import { Button, Col, Container, Input, Row } from "reactstrap";
import { Link } from "react-router-dom";
import { DynamicTable } from "@workspace/common";
import {
  ACCOUNT_INITIAL_OPTIONS,
  generateAccountFields,
} from "../../pages/AccountListing/accountListingConstants";
import "./DynamicTableWrapper.scss";
import { useUserAuth } from "@workspace/login";
import { toast } from "react-toastify";
import { DateHelper, DynamicTableHelper } from "@workspace/common";
import {
  fetchAccountCustomView,
  selectAccountCustomView,
  deleteAccountCustomView,
  resetAccountCustomView,
  deleteAccountDefaultCustomView,
} from "../../store/account/action";
import { DeleteCustomModal, FilterBubbles } from "@workspace/common";
import { useDispatch, useSelector } from "react-redux";
import TableRowsPerPageWithNav from "@workspace/common/src/Components/DynamicTable/TableRowsPerPageWithNav";
import TableItemDisplay from "@workspace/common/src/Components/DynamicTable/TableItemDisplay";
import {
  fetchAccounts,
  deleteAccounts,
  deleteAccountsReset,
  unselectAccountCustomView,
} from "../../store/account/action";
import { TooltipWrapper } from "@workspace/common";
import { getAccounts } from "../../helpers/backend_helper";
import AccountCustomViewCanvas from "../../pages/AccountListing/AccountCustomViewCanvas/AccountCustomViewCanvas";

const DynamicTableWrapper = ({
  data,
  pageInfo,
  pageRequest,
  pageRequestSet,
  config,
  search,
  setSearch,
  optGroup,
  setCustomConfigData,
  header,
  activeRow,
  setActiveRow,
  setTableConfig,
  ...props
}) => {
  // ================== Custom Render ==================
  const customRenderList = [
    {
      names: ["updatedAt", "createdAt"],
      render: (data, opt) =>
        DateHelper.formatDateStandard2(
          DynamicTableHelper.getDynamicNestedResultForExport(data, opt?.name) ||
            ""
        ),
    },
  ];
  // ==================================================
  const { Permission, checkAllPermission } = useUserAuth();
  const dispatch = useDispatch();
  const [customViewDropdownOpen, setCustomViewDropdownOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingCustomViewId, setDeletingCustomViewId] = useState(null);
  const [defaultViewEnabled, setDefaultViewEnabled] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const accountsMeta = useSelector(
    (state) => state.AccountReducer.accountsMeta
  );
  const allAccountCustomViews = useSelector(
    (state) => state?.AccountReducer?.accountCustomViews
  );

  const deleteAccountsMeta = useSelector(
    (state) => state.AccountReducer.deleteAccountsMeta
  );

  const accountDefaultCustomView = useSelector(
    (state) => state?.AccountReducer?.accountDefaultCustomView
  );

  const accountForm = useSelector((state) => state.AccountFormReducer.form);
  const commercialForm = useSelector(
    (state) => state.AccountFormReducer.commercialForm
  );

  // Delete modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchAccountCustomView());
    return () => {
      dispatch(resetAccountCustomView());
      dispatch(deleteAccountDefaultCustomView());
    };
  }, []);

  const handleSelectCustomView = (id) => {
    dispatch(selectAccountCustomView({ id: id }));
  };

  const handleDeleteButtonClick = (id) => {
    setDeleteModalOpen(true);
    setDeletingCustomViewId(id);
  };

  const handleDeleteCustomView = (id) => {
    const customView = allAccountCustomViews.find((view) => view?.id === id);
    dispatch(deleteAccountCustomView({ id: id }));
    setDeleteModalOpen(false);
    setDeletingCustomViewId(null);
    if (customView?.selected) {
      enableDefaultView();
    }
  };

  useEffect(() => {
    const accountFields = generateAccountFields(accountForm, commercialForm);
    if (allAccountCustomViews != null && allAccountCustomViews.length > 0) {
      const selectedCustomView = allAccountCustomViews?.find(
        (customView) => customView?.selected
      );
      if (selectedCustomView && accountFields && accountFields?.length > 0) {
        const selectedGroup = selectedCustomView?.columnName?.split(",");
        const selectedObjects = selectedGroup?.map((value) => {
          return accountFields?.find((option) => option?.value === value);
        });
        if (selectedObjects.length > 0) {
          setCustomConfigData(selectedObjects);
        }
        pageRequestSet.setFilterData(selectedCustomView?.filters);
        setDefaultViewEnabled(false);
      } else if (!selectedCustomView) {
        if (
          accountDefaultCustomView?.columnName &&
          accountDefaultCustomView?.columnName?.length > 0
        ) {
          const selectedObjects = accountDefaultCustomView?.columnName?.map(
            (value) => {
              return accountFields?.find((option) => option?.value === value);
            }
          );
          setCustomConfigData(selectedObjects);
        } else {
          enableDefaultView();
        }

        pageRequestSet.setFilterData(accountDefaultCustomView?.filters ?? []);
        pageRequestSet.setExternalFilterData(
          accountDefaultCustomView?.externalFilters ?? []
        );
      }
    }

    if (allAccountCustomViews != null && allAccountCustomViews.length === 0) {
      if (
        accountDefaultCustomView?.columnName &&
        accountDefaultCustomView?.columnName?.length > 0
      ) {
        const selectedObjects = accountDefaultCustomView?.columnName?.map(
          (value) => {
            return accountFields?.find((option) => option?.value === value);
          }
        );
        setCustomConfigData(selectedObjects);
      } else {
        setCustomConfigData(ACCOUNT_INITIAL_OPTIONS);
      }
      pageRequestSet.setFilterData(accountDefaultCustomView?.filters ?? []);
      pageRequestSet.setExternalFilterData(
        accountDefaultCustomView?.externalFilters ?? []
      );
    }
  }, [
    allAccountCustomViews,
    accountDefaultCustomView,
    accountForm,
    commercialForm,
  ]);

  const enableDefaultView = () => {
    setCustomConfigData(ACCOUNT_INITIAL_OPTIONS);
    pageRequestSet.setFilterData(null);
    setDefaultViewEnabled(true);
  };

  const handleEportExcel = async () => {
    let exportData = null;
    try {
      const payload = {
        ...pageRequest,
        isDownload: true,
        filters: props?.accountFilterBubble,
      };
      const resp = await getAccounts(payload);
      exportData = resp?.data?.accounts;
      if (activeRow?.length > 0) {
        exportData = exportData.filter((item) => activeRow.includes(item?.id));
      }
    } catch (e) {}

    DynamicTableHelper.handleExportExcel(
      "Accounts",
      exportData,
      config.slice(2, -1),
      customRenderList,
      true
    );
  };

  const handleDelete = () => {
    if (activeRow?.length === 0) {
      toast.error("Please select at least one record to delete.");
      return;
    }
    setIsDeleteModalOpen(true);
  };

  // Modal Delete accounts
  const confirmDelete = () => {
    dispatch(deleteAccounts(activeRow));
  };

  useEffect(() => {
    if (deleteAccountsMeta?.isSuccess) {
      dispatch(deleteAccountsReset());
      toast.success("Account deleted successfully");
      setIsDeleteModalOpen(false);
      if (pageRequest?.searchFields?.length > 0) {
        setActiveRow([]);
        dispatch(
          fetchAccounts(DynamicTableHelper.cleanPageRequest(pageRequest))
        );
      }
    }
  }, [deleteAccountsMeta?.isSuccess]);

  const handleDefaultViewSelection = () => {
    dispatch(unselectAccountCustomView());
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col lg={12}>
              <div
                className="listjs-table d-flex flex-column"
                style={{
                  height: "calc(100vh - 160px)",
                }}
              >
                <Row className="mb-2">
                  <Col>
                    <h4 className="d-flex gap-1">
                      <span>{header}</span>
                      <span> {` (${pageInfo?.totalElements || 0})`}</span>
                    </h4>
                  </Col>
                </Row>
                <Row className="d-flex mb-2">
                  <Col className="d-flex align-items-center gap-3">
                    {setSearch && (
                      <div className="search-box">
                        <form onSubmit={pageRequestSet.setSearchTerm}>
                          <Input
                            type="text"
                            placeholder="Search"
                            className="form-control search"
                            value={search}
                            style={{
                              width: "250px",
                              height: "40px",
                              borderColor: "#d3d3d3",
                            }}
                            onChange={(e) => setSearch(e.target.value)}
                          />
                        </form>
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    )}
                  </Col>
                  <Col>
                    <div className="d-flex column-gap  gap-1 justify-content-end align-items-center">
                      <TableItemDisplay pageInfo={pageInfo} />
                      <div
                        style={{
                          width: "2px",
                          height: "20px",
                          backgroundColor: "#adb5bd",
                          marginLeft: "12px",
                        }}
                      ></div>
                      <TableRowsPerPageWithNav
                        pageInfo={pageInfo}
                        pageRequestSet={pageRequestSet}
                      />
                      <div>
                        <TooltipWrapper tooltipText="Export to Excel">
                          <Button
                            className="al-btn al-dl"
                            onClick={handleEportExcel}
                          >
                            <span className="mdi mdi-download-outline"></span>
                          </Button>
                        </TooltipWrapper>
                        {checkAllPermission([Permission.ACCOUNT_DELETE]) && (
                          <TooltipWrapper tooltipText="Delete Multiple">
                            <Button className="al-btn" onClick={handleDelete}>
                              <span className="mdi mdi-delete-outline"></span>
                            </Button>
                          </TooltipWrapper>
                        )}
                        <TooltipWrapper tooltipText="Filter">
                          <Button
                            className="al-btn al-filter"
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                          >
                            <span className="mdi mdi-filter-variant"></span>
                          </Button>
                        </TooltipWrapper>
                      </div>
                      {checkAllPermission([Permission.ACCOUNT_WRITE]) && (
                        <Link to="/accounts/create" style={{ color: "black" }}>
                          <TooltipWrapper tooltipText="Create Account">
                            <Button className="al-create-btn">
                              <span className="mdi mdi-plus"></span>
                            </Button>
                          </TooltipWrapper>
                        </Link>
                      )}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col
                    className="d-flex flex-row justify-content-between align-items-center"
                    lg={12}
                    style={{ fontSize: "12px" }}
                  >
                    <FilterBubbles
                      setBubbleExternalFilter={props?.setAccountFilterBubble}
                      bubbleExternalFilter={props?.accountFilterBubble}
                    />
                    <div>
                      <span className="fw-semibold me-1">Now Viewing:</span>
                      <span>
                        {props.accountCustomView &&
                        props.accountCustomView != -1
                          ? props.accountCustomView?.name
                          : "Default View"}
                      </span>
                    </div>
                  </Col>
                </Row>
                <DynamicTable
                  config={config}
                  data={data}
                  pageRequestSet={pageRequestSet}
                  pageInfo={pageInfo}
                  isLoading={accountsMeta?.isLoading ?? true}
                  freezeHeader={true}
                  activeRow={activeRow}
                  setTableConfig={setTableConfig}
                  pageRequest={pageRequest}
                  tableHeight="100%"
                />
              </div>
            </Col>
          </Row>
          <DeleteCustomModal
            isOpen={deleteModalOpen}
            setIsOpen={setDeleteModalOpen}
            confirmDelete={() => handleDeleteCustomView(deletingCustomViewId)}
            header="Delete Custom View Confirmation"
            deleteText={`Are you sure you want to delete this custom view?`}
            confirmButtonText="Delete"
            isLoading={false}
          />
          <DeleteCustomModal
            isOpen={isDeleteModalOpen}
            setIsOpen={setIsDeleteModalOpen}
            confirmDelete={confirmDelete}
            header="Delete Account"
            deleteText={"Are you sure you would like to delete this account?"}
            isLoading={deleteAccountsMeta?.isLoading}
          />
          <AccountCustomViewCanvas
            isFilterOpen={isFilterOpen}
            setIsFilterOpen={setIsFilterOpen}
            accountCustomView={props?.accountCustomView}
            setAccountCustomView={props?.setAccountCustomView}
            accountDefaultCustomView={props?.accountDefaultCustomView}
            setAccountBubbleFilter={props?.setAccountFilterBubble}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DynamicTableWrapper;
